// APA (Advance Provisioning Allowance) - shared rules for the APA page, the server (PDFs, AI) and the tests.
// The database (sql/apa.sql) enforces the same rules; amount_apa is calculated there exactly like toApa() here.

export const CATEGORIES = [
  ['fuel', 'Fuel'], ['provisions', 'Provisions'], ['beverages', 'Beverages'], ['marina', 'Marina / berthing'], ['port_fees', 'Port fees'],
  ['ports', 'Ports'], ['communications', 'Communications'], ['guest_activities', 'Guest activities'], ['transport', 'Transport'],
  ['laundry', 'Laundry'], ['misc', 'Misc'], ['bank_charges', 'Bank charges'], ['other', 'Other'],
]
export const CAT_LABEL = Object.fromEntries(CATEGORIES)
export const TXN_TYPES = [
  ['expenditure', 'Expenditure'], ['funds_in', 'Funds in (transfer / top-up)'], ['refund', 'Refund / credit note'], ['stock_return', 'Returned to boat stock'],
  ['cash_withdrawal', 'Cash withdrawal'], ['cash_deposit', 'Cash deposit'], ['transfer', 'Transfer between accounts'], ['bank_charge', 'Bank charges'],
]
export const TXN_LABEL = Object.fromEntries(TXN_TYPES)
export const ACCOUNT_KINDS = [['bank', 'Bank account'], ['card', 'Card'], ['voly_card', 'Voly card'], ['cash', 'Cash']]
export const KIND_LABEL = Object.fromEntries(ACCOUNT_KINDS)
export const CHARGED_TO = [['apa', 'This APA'], ['owner', 'Owner'], ['vessel_ops', 'Vessel ops']]
export const CHARGED_LABEL = Object.fromEntries(CHARGED_TO)
export const DEFAULT_LOW_PCT = 20

const CREDIT = new Set(['funds_in', 'refund', 'stock_return'])
const TRANSFER = new Set(['cash_withdrawal', 'cash_deposit', 'transfer'])   // money moves between two accounts, the total stays
export const isCredit = t => CREDIT.has(t)
export const isTransfer = t => TRANSFER.has(t)

const num = v => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v))
export const round2 = x => Math.round((Number(x) || 0) * 100 + Number.EPSILON * 100) / 100
// value in the APA currency (same as the database: round(amount * fx_rate, 2))
export const toApa = (amount, fx) => round2(Number(amount) * (num(fx) || 1))
const val = t => (t.amount_apa != null ? Number(t.amount_apa) : toApa(t.amount, t.fx_rate))
export const money = (v, cur) => (v == null ? '' : (v < 0 ? '-' : '') + Math.abs(round2(v)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (cur ? ' ' + cur : ''))
export const accountLabel = a => (a ? (a.name || KIND_LABEL[a.kind] || 'Account') + (a.last4 ? ' ••' + a.last4 : '') : '')
export const lowPctOf = (apa, settings) => num(apa && apa.low_pct) ?? num(settings && settings.low_pct) ?? DEFAULT_LOW_PCT

// balance of every account (in the APA currency)
export function accountBalances(accounts, txns) {
  const bal = Object.fromEntries((accounts || []).map(a => [a.id, 0]))
  for (const t of txns || []) {
    const v = val(t)
    if (TRANSFER.has(t.txn_type)) {
      if (t.account_id in bal) bal[t.account_id] -= v
      if (t.to_account_id in bal) bal[t.to_account_id] += v
    } else if (t.account_id in bal) bal[t.account_id] += CREDIT.has(t.txn_type) ? v : -v
  }
  for (const k in bal) bal[k] = round2(bal[k])
  return bal
}

// everything the page header, the statement, the AI and the morning briefing show
export function apaSummary(apa, accounts, txns, settings) {
  const kind = Object.fromEntries((accounts || []).map(a => [a.id, a.kind]))
  let funds = 0, card = 0, cashExp = 0, bankExp = 0, cashMoves = 0, charges = 0
  const byCategory = {}
  for (const t of txns || []) {
    const v = val(t), k = kind[t.account_id]
    if (t.txn_type === 'funds_in') { funds += v; continue }
    if (t.txn_type === 'bank_charge') { charges += v; byCategory.bank_charges = (byCategory.bank_charges || 0) + v; continue }
    if (TRANSFER.has(t.txn_type)) {
      if (kind[t.to_account_id] === 'cash') cashMoves += v
      if (k === 'cash') cashMoves -= v
      continue
    }
    const s = t.txn_type === 'expenditure' ? v : -v      // refund / stock return = credit against what was spent
    if (k === 'cash') cashExp += s; else if (k === 'bank') bankExp += s; else card += s
    const c = t.category || 'other'
    byCategory[c] = (byCategory[c] || 0) + s
  }
  const bal = accountBalances(accounts, txns)
  let cashBal = 0, accountBal = 0
  for (const a of accounts || []) { if (a.kind === 'cash') cashBal += bal[a.id]; else accountBal += bal[a.id] }
  const spent = card + cashExp + bankExp + charges
  const balance = round2(funds - spent)
  const pctLeft = funds > 0 ? Math.round(balance / funds * 1000) / 10 : null
  const lowPct = lowPctOf(apa, settings)
  for (const c in byCategory) byCategory[c] = round2(byCategory[c])
  return {
    currency: (apa && apa.currency) || 'EUR', funds: round2(funds), card: round2(card), cashExp: round2(cashExp), bankExp: round2(bankExp),
    cashMoves: round2(cashMoves), charges: round2(charges), spent: round2(spent), balance, cashBal: round2(cashBal), accountBal: round2(accountBal),
    toReturn: round2(cashBal + accountBal), pctLeft, lowPct, low: funds > 0 && pctLeft < lowPct, balances: bal, byCategory,
  }
}

// statement summary, in the order of the charter statement
export function statementLines(s) {
  return [
    ['Total APA funds', s.funds], ['Card expenditure', s.card], ['Cash withdrawals and deposits', s.cashMoves], ['Cash expenditure', s.cashExp],
    ['Chargeable invoices / wire payments', s.bankExp], ['Bank charges', s.charges], ['Cash balance to return', s.cashBal],
    ['APA balance on account', s.accountBal], [s.toReturn < 0 ? 'Total to be paid by the charterer' : 'Total APA funds to return', Math.abs(s.toReturn)],
  ]
}
export function categoryLines(s) { return CATEGORIES.filter(([k]) => s.byCategory[k]).map(([k, l]) => [l, s.byCategory[k]]) }

// Excel / CSV export: one row per account movement (a cash withdrawal is two rows: out of the bank, into cash)
export const EXPORT_COLUMNS = ['Date', 'Account type', 'Account name', 'Txn type', 'Category', 'Description', 'Supplier', 'Document no', 'Currency', 'Transaction amount', 'Report currency', 'Report value', 'TXN no']
export function exportRows(apa, accounts, txns) {
  const acc = Object.fromEntries((accounts || []).map(a => [a.id, a]))
  const cur = (apa && apa.currency) || 'EUR', out = []
  const row = (t, a, sign) => [t.txn_date, KIND_LABEL[a && a.kind] || '', accountLabel(a), TXN_LABEL[t.txn_type] || t.txn_type, t.txn_type === 'bank_charge' ? CAT_LABEL.bank_charges : (CAT_LABEL[t.category] || ''),
    [t.description, t.recharged ? 'Paid by vessel ops, recharged to APA' + (t.vessel_doc_no ? ' (' + t.vessel_doc_no + ')' : '') : ''].filter(Boolean).join(' - '),
    t.supplier || '', t.doc_no || '', t.currency || cur, round2(sign * Number(t.amount)), cur, round2(sign * val(t)), t.txn_no || '']
  for (const t of [...(txns || [])].sort((a, b) => String(a.txn_date).localeCompare(String(b.txn_date)) || String(a.created_at || '').localeCompare(String(b.created_at || '')))) {
    if (TRANSFER.has(t.txn_type)) { out.push(row(t, acc[t.account_id], -1)); out.push(row(t, acc[t.to_account_id], 1)) }
    else out.push(row(t, acc[t.account_id], CREDIT.has(t.txn_type) ? 1 : -1))
  }
  return out
}
export function toCsv(rows, header = EXPORT_COLUMNS) {
  const q = v => { const s = v == null ? '' : String(v); return /[",\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  return '﻿' + [header, ...rows].map(r => r.map(q).join(',')).join('\r\n')
}

// ---- fuel charge ----
const hrs = v => { const m = String(v == null ? '' : v).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? Number(m[0]) : null }
const dOf = r => String(r.date || r.txn_date || '').slice(0, 10)
// engine log reading for the charter start (last one on or before the day, else the first after) or end (last one on or before the day)
export function pickReading(rows, day, which) {
  const list = (rows || []).filter(r => /^\d{4}-\d{2}-\d{2}/.test(dOf(r))).sort((a, b) => dOf(a).localeCompare(dOf(b)) || (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
  const before = list.filter(r => dOf(r) <= day)
  const r = before.length ? before[before.length - 1] : (which === 'start' ? list[0] : null)
  if (!r) return null
  return { date: dOf(r), me_port: hrs(r.me_port_hours), me_stbd: hrs(r.me_stbd_hours), gen_port: hrs(r.gen_port_hours), gen_stbd: hrs(r.gen_stbd_hours), fuel_total: hrs(r.fuel_total) }
}
// last bunkering invoice up to the charter end: price per litre + its reference
export function lastBunkerPrice(bunkerings, day) {
  const b = (bunkerings || []).filter(x => !day || String(x.date).slice(0, 10) <= day).sort((x, y) => String(y.date).localeCompare(String(x.date)))
    .find(x => num(x.price_per_l) != null || (num(x.total_cost) != null && num(x.quantity_l)))
  if (!b) return null
  const p = num(b.price_per_l) ?? round2(Number(b.total_cost) / Number(b.quantity_l) * 1000) / 1000
  return { price_per_l: p, currency: (b.currency || 'EUR').toUpperCase(), ref: b.bdn_number || null, date: String(b.date).slice(0, 10), supplier: b.supplier || null, bunkering_id: b.id || null }
}
// hours x L/h (or litres) x price of the last bunkering; extras = tender / jet ski lines
export function fuelCharge({ start, end, rates, price, extras, method = 'hours', bunkeredL = 0, fx = 1 }) {
  const lines = []
  if (method === 'litres') {
    const a = start && start.fuel_total, b = end && end.fuel_total
    if (a != null && b != null) lines.push({ label: 'Fuel used (tank ' + a + ' L at start + ' + (bunkeredL || 0) + ' L bunkered - ' + b + ' L at end)', litres: round2(a + (bunkeredL || 0) - b) })
  } else {
    for (const [k, label, rate] of [['me_port', 'Main engine port', 'me_lph'], ['me_stbd', 'Main engine stbd', 'me_lph'], ['gen_port', 'Generator port', 'gen_lph'], ['gen_stbd', 'Generator stbd', 'gen_lph']]) {
      const a = start && start[k], b = end && end[k], lph = num(rates && rates[rate])
      if (a == null || b == null) continue
      const h = round2(b - a)
      if (h <= 0) continue
      lines.push({ label, hours_start: a, hours_end: b, hours: h, lph, litres: lph != null ? round2(h * lph) : null })
    }
  }
  for (const x of extras || []) {
    const l = num(x.litres) ?? (num(x.hours) != null && num(x.lph) != null ? round2(num(x.hours) * num(x.lph)) : null)
    if (x.label && l != null && l > 0) lines.push({ label: String(x.label).slice(0, 60), hours: num(x.hours), lph: num(x.lph), litres: l, extra: true })
  }
  const missing = lines.filter(l => l.litres == null).map(l => l.label + ': litres per hour not set')
  const litres = round2(lines.reduce((s, l) => s + (l.litres || 0), 0))
  const p = price && num(price.price_per_l)
  const amount = p != null ? round2(litres * p) : null
  return { method, lines, litres, price_per_l: p, currency: (price && price.currency) || null, ref: (price && price.ref) || null, price_date: (price && price.date) || null,
    supplier: (price && price.supplier) || null, amount, fx_rate: num(fx) || 1, missing: p == null ? [...missing, 'No bunkering invoice with a price per litre'] : missing }
}

// ---- reconciliation worksheet ----
const day = t => (t ? String(t).slice(0, 10) : null)
const overlapNights = (a1, a2, b1, b2) => { const s = a1 > b1 ? a1 : b1, e = a2 < b2 ? a2 : b2; return s && e && e > s ? Math.round((Date.parse(e) - Date.parse(s)) / 864e5) : 0 }
export function worksheetPrefill({ apa, engineRows, trips, portCalls, marinas, txns, tenderHours }) {
  const s = day(apa.period_start), e = day(apa.period_end)
  const r0 = pickReading(engineRows, s, 'start'), r1 = pickReading(engineRows, e, 'end')
  const eng = {}
  for (const [k, label] of [['me_port', 'Main engine port'], ['me_stbd', 'Main engine stbd'], ['gen_port', 'Generator port'], ['gen_stbd', 'Generator stbd']]) {
    const a = r0 && r0[k], b = r1 && r1[k]
    if (a != null || b != null) eng[k] = { label, start: a, end: b, total: a != null && b != null ? round2(b - a) : null }
  }
  const inP = d => d && (!s || d >= s) && (!e || d <= e)
  const tr = (trips || []).filter(t => inP(day(t.dep_actual || t.dep_planned)) || inP(day(t.arr_actual || t.arr_planned)))
  const nm = round2(tr.reduce((x, t) => x + (Number(t.distance_nm) || 0), 0))
  const ports = []
  const addPort = p => { const n = String(p || '').trim(); if (n && !ports.some(x => x.toLowerCase() === n.toLowerCase())) ports.push(n) }
  tr.forEach(t => { addPort(t.from_port); (Array.isArray(t.legs) ? t.legs : []).forEach(l => addPort(l && (l.to || l.port || l.to_port))); addPort(t.to_port) })
  ;(portCalls || []).filter(p => inP(day(p.eta_date)) || inP(day(p.etd_date))).sort((a, b) => String(a.eta_date).localeCompare(String(b.eta_date))).forEach(p => addPort(p.port))
  const dockage = (marinas || []).map(m => {
    const nights = overlapNights(day(m.arrival_date), day(m.departure_date), s, e)
    if (!nights) return null
    const paid = (txns || []).filter(t => t.marina_booking_id === m.id && t.txn_type === 'expenditure').reduce((x, t) => x + val(t), 0)
    return { location: [m.marina_name, m.location].filter(Boolean).join(', '), nights, cost: paid ? round2(paid) : (num(m.rate) != null ? round2(num(m.rate) * nights) : null), currency: paid ? apa.currency : (m.currency || apa.currency) }
  }).filter(Boolean)
  return { engine: eng, engine_dates: { start: r0 && r0.date, end: r1 && r1.date }, nautical_miles: nm || null, ports, dockage, tender_hours: tenderHours ?? null }
}

// ---- top-up request (draft only) ----
export function suggestTopUp(s, apa, today) {
  const st = day(apa.period_start), en = day(apa.period_end), t = today || new Date().toISOString().slice(0, 10)
  const upto = en && en < t ? en : t
  const daysDone = st ? Math.max(1, Math.round((Date.parse(upto) - Date.parse(st)) / 864e5) + 1) : 1
  const daysLeft = en ? Math.max(0, Math.round((Date.parse(en) - Date.parse(t)) / 864e5)) : 0
  const perDay = s.spent / daysDone
  const need = perDay * (daysLeft + 1) + s.funds * s.lowPct / 100 - s.balance
  return Math.max(1000, Math.ceil(Math.max(need, s.funds * 0.25) / 1000) * 1000)
}
export const isEmail = x => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(String(x || '').trim())
export function topUpDraft({ charter, apa, summary, vesselName, amount, captain }) {
  const s = summary, cur = s.currency, ref = (charter && charter.charter_ref) || 'the charter'
  const to = [charter && charter.broker_contact].find(isEmail) || ''
  const subject = 'APA top-up request - ' + (vesselName || 'the vessel') + ' - ' + ref
  const body = [
    'Dear ' + ((charter && (charter.broker || charter.lead_guest)) || 'Sir / Madam') + ',', '',
    'For ' + ref + (apa.period_start ? ' (' + day(apa.period_start) + ' to ' + day(apa.period_end) + ')' : '') + ' the APA balance is now ' + money(s.balance, cur) + ', ' + (s.pctLeft == null ? '' : s.pctLeft + '% of ') + 'the ' + money(s.funds, cur) + ' received so far.', '',
    'Spent to date: ' + money(s.spent, cur) + '. Largest items: ' + Object.entries(s.byCategory).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => (CAT_LABEL[k] || k) + ' ' + money(v, cur)).join(', ') + '.', '',
    'To cover the rest of the charter we kindly request a top-up of ' + money(amount, cur) + ' to the APA account.',
    'A detailed statement with all receipts is available on request and will follow at the end of the charter.', '',
    'Kind regards,', captain || 'Captain', vesselName || '',
  ].join('\n')
  return { to, subject, body }
}

// ---- AI receipt reading: clean the model's answer (nothing is saved from it) ----
// "1.234,50" / "1,234.50" / "184,5" / 184.5 -> number (the last separator followed by 1-2 digits is the decimal one)
export function parseAmount(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  let s = String(v ?? '').replace(/[^\d.,-]/g, '')
  if (!s) return null
  const m = s.match(/^(.*)[.,](\d{1,2})$/)
  s = m ? m[1].replace(/[.,]/g, '') + '.' + m[2] : s.replace(/[.,]/g, '')
  return num(s)
}
export function normalizeReceipt(o, fallbackCurrency) {
  o = o || {}
  const amt = parseAmount(o.amount)
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(o.date || '')) ? o.date : null
  const cur = /^[A-Za-z]{3}$/.test(String(o.currency || '')) ? String(o.currency).toUpperCase() : (fallbackCurrency || 'EUR')
  const cat = CAT_LABEL[o.category] ? o.category : 'other'
  return { txn_date: date, supplier: o.supplier ? String(o.supplier).slice(0, 120) : null, amount: amt != null && amt > 0 ? round2(amt) : null, currency: cur, category: cat,
    description: o.description ? String(o.description).slice(0, 300) : null, doc_no: o.doc_no ? String(o.doc_no).slice(0, 60) : null }
}

// ---- APA files: crew-docs/apa/<vessel id>/<file name> (same rule as apa_file_ok / apa_can_read_file in sql/apa.sql) ----
export const apaFileName = name => String(name || 'file').replace(/[^\w.-]/g, '_').replace(/\.{2,}/g, '_').slice(-120)
// the stored PATH apa/<vessel id>/<name> (new uploads) or the old full link of this project
export function apaFileUrlOk(url, base, vesselId) {
  const u = String(url || ''), dir = 'apa/' + vesselId + '/', pre = String(base || '').replace(/\/$/, '') + '/storage/v1/object/public/crew-docs/' + dir
  const tail = u.startsWith(dir) ? u.slice(dir.length) : !!base && u.startsWith(pre) ? u.slice(pre.length) : null
  return !!vesselId && tail != null && !u.includes('..') && /^[A-Za-z0-9_.-]{1,200}$/.test(tail)
}

// ---- crew gratuity (tips): a GUIDE from the charter fee, and what was received - never part of the APA balance ----
export const DEFAULT_GRATUITY = { pct: 10, range: [5, 10, 15] }
export const GRATUITY_METHODS = [['cash', 'Cash'], ['transfer', 'Bank transfer']]
export function gratuityGuide(charter, settings) {
  const fee = num(charter && charter.charter_rate)
  if (fee == null || fee <= 0) return null
  const cur = String((charter && charter.currency) || 'EUR').toUpperCase()
  let range = (settings && Array.isArray(settings.gratuity_range) ? settings.gratuity_range : DEFAULT_GRATUITY.range).map(Number).filter(x => Number.isFinite(x) && x > 0 && x <= 50)
  if (!range.length) range = DEFAULT_GRATUITY.range
  range = [...new Set(range)].sort((x, y) => x - y)
  const suggested = num(settings && settings.gratuity_pct) ?? DEFAULT_GRATUITY.pct
  return { fee, currency: cur, suggested, lines: range.map(p => ({ pct: p, amount: round2(fee * p / 100), suggested: p === suggested })),
    text: 'Crew gratuity guide: ' + range.map(p => p + '% = ' + money(fee * p / 100, cur)).join(', ') + ' of the charter fee (' + money(fee, cur) + ') - customary, at the charterer\'s discretion.' }
}
// received gratuities, per currency (kept apart from the APA)
export function gratuityTotals(rows) {
  const t = {}
  for (const g of rows || []) { const c = String(g.currency || 'EUR').toUpperCase(); t[c] = round2((t[c] || 0) + Number(g.amount || 0)) }
  return t
}

// ---- AI receipt -> entry form. overwrite = false (automatic, after a receipt is added): only EMPTY fields - a field still at
// its new-form default (today's date, APA currency, default category) counts as empty; true ("Read with AI"): every field read
export const RECEIPT_FIELDS = ['txn_date', 'supplier', 'amount', 'currency', 'category', 'doc_no', 'description']
export function fillReceipt(form, p, { overwrite = false, defaults = {}, apaCurrency, rateFor } = {}) {
  const next = { ...form }, filled = []
  const empty = k => { const v = form[k]; return v == null || String(v).trim() === '' || (defaults[k] != null && String(v) === String(defaults[k])) }
  for (const k of RECEIPT_FIELDS) {
    const v = p && p[k] != null ? String(p[k]).trim() : ''
    if (!v || (!overwrite && !empty(k)) || String(form[k] ?? '') === v) continue
    next[k] = v; filled.push(k)
  }
  // a foreign currency read from the receipt: suggest the rate when none is typed
  if (next.currency && apaCurrency && next.currency !== apaCurrency && !String(form.fx_rate || '').trim() && rateFor) {
    const r = rateFor(next.currency); if (r) next.fx_rate = String(r)
  }
  return { form: next, filled }
}

// ---- petty cash vouchers ----
export const VOUCHER_RE = /^PC-\d{4}-\d{4,}$/
export function voucherOk(v) {
  const miss = []
  if (!(num(v.amount) > 0)) miss.push('amount')
  if (!String(v.purpose || '').trim()) miss.push('what it is for')
  if (!String(v.approved_by_name || '').trim()) miss.push('approved by')
  if (!String(v.received_by_name || '').trim()) miss.push('received by')
  if (!(Array.isArray(v.approver_sig) && v.approver_sig.length)) miss.push('approver signature')
  if (!v.receiver_refused && !(Array.isArray(v.receiver_sig) && v.receiver_sig.length)) miss.push('receiver signature (or "refused to sign")')
  if (v.charged_to === 'apa' && !v.apa_id) miss.push('APA')
  if (v.charged_to === 'apa' && !v.account_id) miss.push('account')
  return miss
}
// signature strokes are stored as [[x, y], ...] lists with x / y from 0 to 1 (top left = 0, 0), 2 decimals
export function cleanStrokes(strokes) {
  return (Array.isArray(strokes) ? strokes : []).slice(0, 60).map(s => (Array.isArray(s) ? s : []).slice(0, 400)
    .map(p => [Math.min(1, Math.max(0, Math.round(Number(p[0]) * 100) / 100)), Math.min(1, Math.max(0, Math.round(Number(p[1]) * 100) / 100))])
    .filter(p => Number.isFinite(p[0]) && Number.isFinite(p[1]))).filter(s => s.length > 1)
}
